import { buildBoardScene, type BoardOpts, type BoardView } from './render/board-gl';
import { newGameState, type GameState, type Side } from './rules/state';
import { aiSetup, aiMover, aiBuyFleet } from './rules/ai';
import { runTurn, resolveCombatSites } from './rules/turn';
import { reachable, pathTo } from './rules/movement';
import { addShip } from './rules/ships';
import { startGame, type PlayHandle } from './ui/game';
import { startCombatViewer, type CombatViewerHandle } from './ui/combat-viewer';
import { showCampaignMap, type CampaignMapHandle } from './ui/campaign';
import { applyBattleResult, battleConfig, CAMP_REGIONS, newCampaign } from './rules/campaign';
import { buildTutorialState } from './rules/tutorial';
import { buildInstantBattle } from './rules/instant';
import { showInstantChooser, type InstantChooserHandle } from './ui/instant';
import { showTitleScreen, type TitleHandle } from './ui/title';
import { ICON_ANCHOR } from './ui/icons';
import { showSettings, type SettingsHandle } from './ui/settings';
import { applyCrt } from './ui/crt';
import { showFps } from './ui/fps';
import {
  clearAllData, clearCampaign, getCrt, getDefaultDifficulty, getFps, loadCampaign, saveCampaign,
} from './ui/save';
import { audio, type AudioEngine } from './audio/engine';
import type { BuyHandle } from './ui/buy';
import type { TutorialController } from './ui/tutorial';

declare global {
  interface Window {
    __ready?: boolean;
    __scenario?: string;
    __drawCalls?: number;
    /** re-render the current scene once (used by E2E + the perf bench) */
    __renderOnce?: () => void;
    /** interactive game handle + board view, exposed for the play E2E */
    __game?: PlayHandle;
    __view?: BoardView;
    /** buy-screen handle while the game is in the buy phase */
    __buy?: BuyHandle | null;
    /** combat-viewer cinematic handle (combat-viewer scenario) */
    __cine?: CombatViewerHandle | null;
    /** campaign-map handle (campaign / campaign-map scenarios) */
    __campaign?: CampaignMapHandle | null;
    /** tutorial controller (tutorial scenario) */
    __tut?: TutorialController | null;
    /** instant-battle chooser handle (instant scenario) */
    __instant?: InstantChooserHandle | null;
    /** title-screen / main-menu handle (default scenario) */
    __title?: TitleHandle | null;
    /** settings-screen handle (settings scenario) */
    __settings?: SettingsHandle | null;
    /** audio engine, exposed for the E2E harness */
    __audio?: AudioEngine;
  }
}

/** A fixed, hand-built battle for the WP-8 combat-viewer cinematic + its golden. */
function combatScenario(): GameState {
  const g = newGameState({ size: 'small', seed: 5 });
  g.ships = [];
  // Combat is now one-ship-per-tile + adjacent fire (2026-07 rework), so each
  // ship needs its OWN tile and the two sides must sit on ADJACENT hexes to form
  // a combat cluster (odd-r offset neighbours). Two well-separated clusters →
  // two distinct sites for the "prev/next duel" controls.
  //
  // Site A — carrier strike around (4,4): two Red carriers + a destroyer vs a
  // Yellow cruiser and destroyer. The carriers boost each other and the DD, so
  // several shots carry a "+1/+2 Air" badge and launch seaplanes.
  addShip(g, 'R', 'CV', 4, 4);
  addShip(g, 'R', 'CV', 3, 4);
  addShip(g, 'R', 'DD', 4, 3);
  addShip(g, 'Y', 'CR', 5, 4);
  addShip(g, 'Y', 'DD', 4, 5);
  // Site B — torpedo + gun duel around (9,8): Red BB + a surfaced Red sub vs two
  // Yellow cruisers (torpedo wakes from the sub, shell arcs from the BB, a sinking).
  addShip(g, 'R', 'BB', 9, 8);
  const sub = addShip(g, 'R', 'SS', 8, 8);
  sub.submerged = false; // surfaced so it fires torpedoes (and can be hit)
  addShip(g, 'Y', 'CR', 9, 9);
  addShip(g, 'Y', 'CR', 8, 9);
  return g;
}

const BOARD_W = 960;
const BOARD_H = 600;

/** Play a deterministic game forward a few turns so the board has something to show. */
function setupGame(seed: number, size: 'small' | 'medium' | 'large', turns: number): GameState {
  const g = newGameState({ size, seed });
  aiSetup(g);
  const move = aiMover();
  for (let i = 0; i < turns && g.winner === null; i++) runTurn(g, move);
  return g;
}

/** Build the (g, opts) for a named board scenario. */
function boardScenario(
  scenario: string,
  size: 'small' | 'medium' | 'large' = 'medium',
): { g: GameState; opts: BoardOpts } {
  const viewSide: Side = 'R';
  if (scenario === 'movement-fog') {
    const g = setupGame(3, size, 4);
    return { g, opts: { width: BOARD_W, height: BOARD_H, viewSide } };
  }
  // 'movement-early' (default board scenario): pick a Red ship, show its reach + a route
  const g = setupGame(7, size, 2);
  const opts: BoardOpts = { width: BOARD_W, height: BOARD_H, viewSide };
  const ship = g.ships.find((s) => s.side === viewSide && !s.done);
  if (ship) {
    opts.selectedId = ship.id;
    const reach = reachable(g, ship);
    opts.reach = Object.values(reach).map((o) => [o.c, o.r, !o.stop] as [number, number, boolean]);
    const target = g.cities
      .filter((c) => c.owner !== viewSide)
      .sort(
        (a, b) =>
          Math.hypot(a.c - ship.c, a.r - ship.r) - Math.hypot(b.c - ship.c, b.r - ship.r),
      )[0];
    if (target) {
      const path = pathTo(g, ship, target.c, target.r);
      if (path && path.length) opts.route = [[ship.c, ship.r], ...path];
    }
  }
  return { g, opts };
}

async function boot(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) throw new Error('#app missing');

  const params = new URLSearchParams(location.search);
  const scenario = params.get('scenario') ?? 'title';
  window.__scenario = scenario;
  window.__audio = audio;

  // Apply the persisted display preferences globally, before any scenario mounts,
  // so they hold across the whole app. (Both default off, so goldens — booted on
  // fresh localStorage — are unaffected.)
  applyCrt(getCrt());
  showFps(getFps());

  // ---- title screen / main menu (default) ----
  if (scenario === 'title') {
    const go = (search: string): void => {
      location.search = search;
    };
    // A new quick battle gets a fresh RANDOM seed so the island layout differs
    // each time (the seed is then saved in G for deterministic replay/resume).
    // Scenario shots/E2E pass ?seed= explicitly or use buy-screen, so they stay
    // deterministic — only this user-initiated "Play" path is randomised.
    const randomSeed = (): number => Math.floor(Math.random() * 0x7fffffff);
    window.__title = showTitleScreen({
      container: app,
      onPlay: () => go(`?scenario=play&fresh=1&seed=${randomSeed()}`),
      onResume: () => go('?scenario=play'),
      onCampaign: () => go('?scenario=campaign'),
      onInstant: () => go('?scenario=instant'),
      onTutorial: () => go('?scenario=tutorial'),
      onSettings: () => {
        // Open settings as a dim overlay over the title (like the in-game gear menu)
        // rather than navigating to a separate screen.
        const h = showSettings({
          container: app,
          overlay: true,
          onBack: () => h.destroy(),
          // Clear ALL saved data (save, campaign, settings) and reload to a fresh title.
          resetLabel: 'Clear Data & Reset',
          onResetProgress: () => { clearAllData(); location.search = '?scenario=title'; },
        });
        window.__settings = h;
      },
    });
    window.__ready = true;
    return;
  }

  // ---- settings screen ----
  if (scenario === 'settings') {
    window.__settings = showSettings({
      container: app,
      onBack: () => {
        location.search = '?scenario=title';
      },
      resetLabel: 'Clear Data & Reset',
      onResetProgress: () => { clearAllData(); location.search = '?scenario=title'; },
    });
    window.__ready = true;
    return;
  }

  if (scenario === 'play' || scenario === 'buy-screen') {
    const sizeParam = (params.get('size') as 'small' | 'medium' | 'large' | null) ?? undefined;
    const seedParam = params.get('seed');
    // ?fresh=1 (and the buy-screen shot) ignore any autosave so the buy screen
    // always boots — the E2E/shots want a deterministic opening.
    const fresh = params.has('fresh') || scenario === 'buy-screen';
    const handle = await startGame(app, {
      size: sizeParam,
      seed: seedParam != null ? Number(seedParam) : undefined,
      difficulty: getDefaultDifficulty(),
      resume: !fresh,
    });
    window.__game = handle;
    window.__view = handle.view;
    window.__buy = handle.buy;
    window.__ready = true;
    return;
  }

  if (scenario === 'combat-viewer') {
    const g = combatScenario();
    const sites = resolveCombatSites(g);
    const host = document.createElement('div');
    host.style.cssText = 'position:relative;width:980px;height:600px;margin:0 auto;';
    app.appendChild(host);
    const handle = await startCombatViewer({
      container: host, sites, viewSide: 'R', seed: 1234, onDone: () => {}, autoplay: false,
    });
    handle.pause();
    handle.seek(Number(params.get('t') ?? '1200'));
    window.__cine = handle;
    window.__ready = true;
    return;
  }

  // ---- Save the Admiral campaign ----
  if (scenario === 'campaign' || scenario === 'campaign-map') {
    // campaign-map is a deterministic still for the golden (a demo progress
    // exercising swayed/against/neutral/locked); campaign loads real progress.
    if (scenario === 'campaign-map') {
      const demo = newCampaign();
      demo.swayed['0-0'] = true; // Avalon swayed (green ✓)
      demo.res['0-1'] = 8; // Brightwater swayed against (red ✗)
      demo.points = 35;
      showCampaignMap({ container: app, progress: demo, onStart: () => {} });
      window.__ready = true;
      return;
    }

    const showMap = (): void => {
      app.innerHTML = '';
      window.__campaign = showCampaignMap({
        container: app,
        progress: loadCampaign(),
        onStart: (region, state) => void startCampaignBattle(region, state),
        onReset: () => {
          clearCampaign();
          showMap();
        },
        onExit: () => {
          location.search = '?scenario=title';
        },
      });
    };

    const startCampaignBattle = async (region: number, state: number): Promise<void> => {
      const camp = loadCampaign();
      const cfg = battleConfig(camp, region, state);
      const g = newGameState({
        size: cfg.size,
        seed: (region * 3 + state) * 1009 + 11,
        budgetR: cfg.budgetR,
        budgetY: cfg.budgetY,
        forceRatio: cfg.ratio,
        forceFavored: 'R',
      });
      aiBuyFleet(g, 'Y'); // enemy buys to its campaign budget; Red buys via the screen
      app.innerHTML = '';
      const handle = await startGame(app, {
        mode: 'campaign',
        state: g,
        label: 'Campaign',
        subLabel: CAMP_REGIONS[region]!.states[state]!.name,
        resume: false,
        onResult: (youWon, gg) => {
          const res = applyBattleResult(camp, region, state, {
            youWon,
            vpR: gg.vp.R,
            vpY: gg.vp.Y,
          });
          saveCampaign(res.campaign);
          return res.message;
        },
        onExit: showMap,
      });
      window.__game = handle;
      window.__view = handle.view;
      window.__buy = handle.buy;
    };

    showMap();
    window.__ready = true;
    return;
  }

  // ---- guided tutorial ----
  if (scenario === 'tutorial') {
    const g = buildTutorialState();
    const handle = await startGame(app, { mode: 'tutorial', state: g, resume: false });
    window.__game = handle;
    window.__view = handle.view;
    window.__tut = handle.tutorial;
    window.__ready = true;
    return;
  }

  // ---- Instant Battles (Jutland / Wolfpack) ----
  if (scenario === 'instant') {
    const showChooser = (): void => {
      app.innerHTML = '';
      window.__instant = showInstantChooser({
        container: app,
        onStart: (choice) => void launchInstant(choice.battle, choice.human, choice.level),
        onExit: () => {
          location.search = '?scenario=title';
        },
      });
    };
    const launchInstant = async (
      battle: 'jutland' | 'wolfpack', human: Side, level: number,
    ): Promise<void> => {
      const g = buildInstantBattle({ battle, human, level });
      app.innerHTML = '';
      const handle = await startGame(app, {
        mode: 'instant', state: g, human, difficulty: level, resume: false,
        label: 'Instant Battle',
        subLabel: battle === 'jutland' ? 'Jutland' : 'Wolfpack',
        onExit: showChooser,
      });
      window.__game = handle;
      window.__view = handle.view;
    };
    // ?battle=&side=&diff= jumps straight in (used by the E2E); else show chooser
    const battleParam = params.get('battle') as 'jutland' | 'wolfpack' | null;
    if (battleParam) {
      const sideParam = (params.get('side') as Side | null) ?? 'R';
      const diffParam = Number(params.get('diff') ?? '6');
      await launchInstant(battleParam, sideParam, diffParam);
    } else {
      showChooser();
    }
    window.__ready = true;
    return;
  }

  const sizeParam = (params.get('size') as 'small' | 'medium' | 'large' | null) ?? undefined;
  const { g, opts } = boardScenario(scenario, sizeParam);
  const view = await buildBoardScene(app, g, opts);
  view.renderOnce();
  window.__renderOnce = () => view.renderOnce();
  window.__drawCalls = view.drawCalls();
  window.__ready = true;
}

/** Friendly error screen instead of a blank page if boot throws. Offers a plain
 *  reload and a "clear saved data" recovery (in case a corrupt save is the cause). */
function showBootError(err: unknown): void {
  document.body.dataset['bootError'] = String(err);
  const app = document.getElementById('app') ?? document.body;
  app.innerHTML = `
    <div style="max-width:460px;margin:0 auto;font-family:Georgia,serif;color:hsl(28, 55.6%, 10.6%);
      background:linear-gradient(180deg,hsl(45, 67.3%, 90.4%),hsl(44, 53.7%, 83.9%) 40%,hsl(40, 46.9%, 71.2%));
      border:2px solid hsl(31, 47.6%, 28.4%);border-top:4px solid hsl(43, 63.2%, 58.4%);border-radius:6px;
      padding:22px 24px;box-shadow:0 8px 30px hsla(0, 0%, 0%, 0.5);text-align:center;">
      <h2 style="margin:0 0 8px;color:hsl(23, 66.7%, 21.2%);">${ICON_ANCHOR} Something went amiss</h2>
      <p style="font-size:13.5px;line-height:1.5;">The game hit an unexpected error while starting up.
      A reload usually fixes it. If not, clearing saved data will start you fresh.</p>
      <div style="display:flex;gap:10px;justify-content:center;margin-top:14px;">
        <button id="ar-err-reload" style="font-family:inherit;font-weight:700;font-size:14px;padding:9px 16px;
          cursor:pointer;border:2px solid hsl(110, 40.6%, 25.1%);border-radius:4px;color:hsl(90, 100%, 95.7%);
          background:linear-gradient(180deg,hsl(105, 47.8%, 64.7%),hsl(105, 38.4%, 43.9%) 55%,hsl(105, 41.7%, 29.6%));
          box-shadow:inset 0 1px 0 hsla(0, 0%, 100%, 0.4),inset 0 -2px 2px hsla(0, 0%, 0%, 0.18),0 1px 2px hsla(0, 0%, 0%, 0.3);
          text-shadow:0 1px 0 hsla(0, 0%, 0%, 0.3);">Reload</button>
        <button id="ar-err-clear" style="font-family:inherit;font-weight:700;font-size:14px;padding:9px 16px;
          cursor:pointer;border:2px solid hsl(4, 48%, 30%);border-radius:4px;color:#fff;
          background:linear-gradient(180deg,hsl(4, 62%, 55%),hsl(4, 62%, 47%) 55%,hsl(4, 62%, 39%));
          box-shadow:inset 0 1px 0 hsla(0, 0%, 100%, 0.4),inset 0 -2px 2px hsla(0, 0%, 0%, 0.22),0 1px 2px hsla(0, 0%, 0%, 0.3);
          text-shadow:0 1px 1px hsla(0, 0%, 0%, 0.45);">Clear data &amp; restart</button>
      </div>
    </div>`;
  document.getElementById('ar-err-reload')?.addEventListener('click', () => location.reload());
  document.getElementById('ar-err-clear')?.addEventListener('click', () => {
    try {
      localStorage.clear();
    } catch {
      /* ignore */
    }
    location.search = '?scenario=title';
  });
}

boot().catch((err) => {
  console.error('boot failed', err);
  showBootError(err);
});
